import React, { useCallback, useMemo } from 'react'
import { Button, Empty, Progress, Select, Typography, Tooltip } from 'antd'
import { IconFont, SvgIcon } from '@/components'
import ProgressContent from '@/components/detail-template/widgets/progress-content'
import EmptyImg from '@/assets/imges/Empty/Empty.png'
import { formatTimeInterval } from '@/utils'
import { statusMap } from './constants'
import styled from 'styled-components'
import { t } from '@/i18n'

const { Text } = Typography

interface TaskContentExpandedProps {
  taskInfo: any
  taskInfoExpanded: any[]
  executeTimeId: string
  infoActiveTab: string
  activeTab: string
  logInfo: any
  failLogInfo: any
  progressInfo: any
  failedCaseList: any[]
  unsentCaseList: any[]
  stableProgressContentList: any[]
  onToggleTime: (timeId: string) => void
  onSetInfoActiveTab: (tab: string) => void
  onSetActiveTab: (tab: string) => void
  onSetLogInfo: (info: any) => void
  onFailedTabClick: () => void
  onUnexecutedTabClick: () => void
}

const TaskContentExpandedComponent: React.FC<TaskContentExpandedProps> = ({
  taskInfo,
  taskInfoExpanded,
  executeTimeId,
  infoActiveTab,
  activeTab,
  logInfo,
  failLogInfo,
  progressInfo,
  failedCaseList,
  unsentCaseList,
  stableProgressContentList,
  onToggleTime,
  onSetInfoActiveTab,
  onSetLogInfo,
  onFailedTabClick,
  onUnexecutedTabClick,
}) => {
  const renderTaskTemplateContent = useCallback(
    () =>
      taskInfoExpanded.slice(taskInfoExpanded.length - 2, taskInfoExpanded.length - 1).map(item => (
        <div key={item.value} className="tab-content">
          {taskInfo[item.value as string]?.length > 0 ? (
            taskInfo[item.value as string].map((v: any, i: number) => (
              <Text key={i} className="template" ellipsis={{ tooltip: true }}>
                {typeof v === 'object' ? `${v.deviceType}(${v.deviceIp})` : v}
              </Text>
            ))
          ) : (
            <Empty image={EmptyImg} />
          )}
        </div>
      )),
    [taskInfo, taskInfoExpanded],
  )

  const renderSecurityDeviceContent = useCallback(
    () =>
      taskInfoExpanded.slice(taskInfoExpanded.length - 1, taskInfoExpanded.length).map(item => (
        <div key={item.value} className="tab-content">
          {taskInfo[item.value as string]?.length > 0 ? (
            taskInfo[item.value as string].map((v: any, i: number) => (
              <Text key={i} className="template" ellipsis={{ tooltip: true }}>
                {typeof v === 'object' ? `${v.deviceType}-${v.deviceName}-${v.deviceIp}` : v}
              </Text>
            ))
          ) : (
            <Empty image={EmptyImg} />
          )}
        </div>
      )),
    [taskInfo, taskInfoExpanded],
  )

  const renderExecutionType = useCallback(() => {
    if (taskInfo?.schedulerType === 'scheduler_type_immediate') {
      return (
        <span>
          {t('立即')}{' '}
          {taskInfo?.execution_list &&
            taskInfo?.execution_list.length > 0 &&
            taskInfo?.execution_list.find(v => v.resultId === taskInfo.id)?.createTime}
        </span>
      )
    } else if (taskInfo?.schedulerType !== 'scheduler_type_immediate') {
      return (
        <span>
          {taskInfo?.schedulerType === 'scheduler_type_reserve' && (
            <span className="type-text">{t('预定')}</span>
          )}
          {taskInfo?.schedulerType === 'scheduler_type_daily' && (
            <span className="type-text">{t('每日')}</span>
          )}
          {taskInfo?.schedulerType === 'scheduler_type_weekly' && (
            <span className="type-text">{t('每周')}</span>
          )}
          {taskInfo?.schedulerType === 'scheduler_type_monthly' && (
            <span className="type-text">{t('每月')}</span>
          )}
          {taskInfo?.schedulerType !== 'scheduler_type_reserve' &&
            taskInfo?.schedulerType !== 'scheduler_type_immediate' && (
              <Select
                className="type-select"
                placeholder={t('点击查看')}
                size="small"
                style={{ width: 180 }}
              >
                {taskInfo?.taskTimeList?.map((v: any, i: number) => (
                  <Select.Option key={i} value={v}>
                    {v}
                  </Select.Option>
                ))}
              </Select>
            )}
        </span>
      )
    }
    return ''
  }, [taskInfo])

  return (
    <TaskContentExpanded>
      {/* 任务信息 */}
      <div className="task-info">
        <div className="sub-title">
          <div>
            <SvgIcon width={16} height={16} name={'data-arrival'} fill={'#fff'} />
            <span className="text">{t('任务信息')}</span>
          </div>
          {taskInfo?.schedulerType !== 'scheduler_type_immediate' && (
            <div style={{ fontSize: 13, fontWeight: 400, display: 'flex', alignItems: 'center' }}>
              <div>{t('执行日期：')} </div>
              <Select
                getPopupContainer={node => node?.parentNode?.parentNode}
                style={{ width: 180, marginRight: 10 }}
                value={executeTimeId}
                onChange={onToggleTime}
              >
                {taskInfo?.executionList?.map((item: any, index: number) => (
                  <Select.Option key={index} value={item.resultId}>
                    {item.createTime}
                  </Select.Option>
                ))}
              </Select>
              <div
                style={{
                  padding: '9px 4px',
                  background: '#ebeef7',
                  borderRadius: '4px',
                  color: '#666666',
                }}
              >
                {taskInfo.order}/{taskInfo.total}
              </div>
            </div>
          )}
        </div>
        <div className="tab-box">
          <div className="left">
            <div className="tab-item active">
              <span>{t('验证模式1')}</span>
            </div>
          </div>
          <div className="right">
            <div
              className={`tab-item ${infoActiveTab === 'taskTemplate' ? 'active' : ''}`}
              onClick={() => onSetInfoActiveTab('taskTemplate')}
            >
              <span>{t('任务模板')}</span>
            </div>
            <div
              className={`tab-item ${infoActiveTab === 'securityDevice' ? 'active' : ''}`}
              onClick={() => onSetInfoActiveTab('securityDevice')}
            >
              <span>{t('安全设备')}</span>
            </div>
          </div>
        </div>
        <div className="content">
          <div className="left">
            <div className="left-item">
              <span className="label">{t('模式名称：')}</span>
              <span>{t('agent → 外部情报')}</span>
            </div>
            <div className="left-item">
              <span className="label">{t('Agent名称：')}</span>
              <Tooltip
                title={`${taskInfo?.verificationInfo?.agentName || '-'} - ${
                  taskInfo?.verificationInfo?.agentIp || '-'
                }`}
                getPopupContainer={() => document.body}
              >
                <span>{taskInfo?.verificationInfo?.agentName || '-'}</span>
              </Tooltip>
            </div>
            <div className="left-item">
              <span className="label">{t('安全域：')}</span>
              <span>{taskInfo?.verificationInfo?.bizAreaName}</span>
            </div>
            <div className="left-item">
              <span className="label">{t('任务分组：')}</span>
              <span>{taskInfo?.groupName}</span>
            </div>
            <div className="left-item">
              <span className="label">{t('执行类型：')}</span>
              <span>{renderExecutionType()}</span>
            </div>
            <div className="left-item">
              <span className="label">{t('执行速率：')}</span>
              <span>
                {taskInfo?.execRate} {t('个/秒')}
              </span>
            </div>
            <div className="left-item">
              <span className="label">{t('优先级：')}</span>
              <span>{taskInfo?.priority}</span>
            </div>
            <div className="left-item">
              <span className="label">{t('超时时间：')}</span>
              <span>
                {taskInfo?.timeOut} {t('秒')}
              </span>
            </div>
            <div className="left-item">
              <span className="label">{t('代理地址：')}</span>
              <span>{taskInfo?.proxyAddress || '-'}</span>
            </div>
            <div className="left-item">
              <span className="label">{t('创建时间：')}</span>
              <span>{taskInfo?.resultCreateTime}</span>
            </div>
          </div>
          <div className="right">
            {infoActiveTab === 'taskTemplate' && renderTaskTemplateContent()}
            {infoActiveTab === 'securityDevice' && renderSecurityDeviceContent()}
          </div>
        </div>
      </div>
      {/* 进度信息 */}
      <div className="progress-info">
        {/* payload 列表 */}
        <div
          style={{
            display: !logInfo.visible && !failLogInfo.visible ? 'block' : 'none',
          }}
        >
          <div className="sub-title">
            <div className="left">
              <SvgIcon
                width={16}
                height={16}
                name={'task-detail-schedule'}
                fill={'#fff'}
                style={{ marginTop: '5px' }}
              />

              <span className="text" style={{ width: 65 }}>
                {t('执行信息')}
              </span>
            </div>
            <div className="create-time">
              <span>
                {t('执行时间：')}
                <span>{progressInfo?.startTime || progressInfo?.createTime}</span>
                <span style={{ margin: '0 4px' }}>{t('至')}</span>
                <span>{progressInfo?.endTime || '-'}</span>
              </span>
              <span style={{ marginLeft: 10 }}>
                {t('时长：')}
                <span style={{ marginLeft: 5 }}>
                  {t('共')}
                  {formatTimeInterval(
                    progressInfo?.startTime || progressInfo?.createTime,
                    progressInfo?.endTime,
                  )}
                </span>
              </span>
            </div>
          </div>
          <div
            style={{ display: 'flex', alignItems: 'center', margin: '15px 0', padding: '0 12px' }}
          >
            <div
              className="status"
              style={{
                background: statusMap[progressInfo.isFinished]?.background,
                color: statusMap[progressInfo.isFinished]?.color,
              }}
            >
              <span>{statusMap[progressInfo.isFinished]?.label}</span>
            </div>
            <Progress
              className="progress"
              strokeColor={{
                '0%': '#ECEBF9',
                '100%': '#5047CC',
              }}
              success={{ percent: -1 }}
              percent={progressInfo?.progressRate}
            />
          </div>
          <div className="execute-box">
            <div className="execute-tab-box">
              <div className="execute-item-title">
                <div
                  className={
                    activeTab === 'failed'
                      ? 'execute-item-title-text active'
                      : 'execute-item-title-text'
                  }
                  onClick={onFailedTabClick}
                >
                  {t('执行失败（')}
                  {failedCaseList?.length || 0}）
                </div>
                <div
                  className={
                    activeTab === 'unexecuted'
                      ? 'execute-item-title-text active'
                      : 'execute-item-title-text'
                  }
                  onClick={onUnexecutedTabClick}
                >
                  {t('未执行（')}
                  {unsentCaseList?.length || 0}）
                </div>
              </div>
              <div className="execute-success-box">
                <span>{t('执行成功：')}</span>
                <span className="success-sum">{progressInfo?.executeSuccessCount}</span>
                <span>{t('个')}</span>
              </div>
            </div>
            <div className="execute-tab-content">
              <ProgressContent
                setLogInfo={onSetLogInfo}
                type={activeTab === 'failed' ? 1 : 2}
                contentList={stableProgressContentList}
              />
            </div>
          </div>
        </div>
        {/* payload 日志 */}
        <div style={{ display: logInfo.visible ? 'block' : 'none' }}>
          <div className="sub-title">
            <div className="left">
              <Button
                className="return-btn"
                icon={<IconFont type="icon-fanhui2" className="return-icon" />}
                onClick={() => onSetLogInfo({ visible: false, content: '' })}
              />

              <span className="text">{t('执行日志：')}</span>
            </div>
          </div>
          <div className="log-content">
            <div className="log-panel">
              <div className="content">
                {logInfo.content && <div className="log-content">{logInfo.content}</div>}
              </div>
            </div>
          </div>
        </div>
      </div>
    </TaskContentExpanded>
  )
}

export default TaskContentExpandedComponent

const TaskContentExpanded = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;

  .task-info {
    flex: 1;
    height: 100%;
    border: 1px solid #e1e3e4;
    margin-right: 16px;
    border-radius: 8px;
    width: 50%;

    .sub-title {
      height: 35px;
      display: flex;
      align-items: center;
      padding-left: 6px;
      justify-content: space-between;

      .text {
        margin-left: 10px;
        font-weight: 500;
        font-size: 16px;
      }
    }
    .tab-box {
      width: 100%;
      height: 32px;
      display: flex;
      align-items: center;
      .left {
        width: 50%;
        min-width: 325px;
        height: 100%;
        display: flex;
        align-items: center;
        border-right: 1px solid #e1e3e4;
      }
      .right {
        width: 50%;
        min-width: 325px;
        height: 100%;
        display: flex;
        align-items: center;
      }
      .tab-item {
        width: 84px;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        &.active {
          color: #4238c9;
          background: #e3e1f5;
        }
        &.disabled {
          color: #333333;
          cursor: not-allowed;
        }
      }
    }

    .content {
      display: flex;
      align-items: center;
      justify-content: space-between;

      .left {
        height: 100%;
        font-size: 12px;
        width: 50%;
        border-right: 1px solid #e1e3e4;
        padding-left: 12px;
        padding-top: 6px;
        padding-right: 12px;

        .left-item {
          margin-bottom: 10px;
          display: flex;
          align-items: center;

          .encode-items-wrapper {
            display: flex;
            flex-wrap: wrap;
            gap: 4px;
            align-items: center;
          }

          .encode-item {
            display: inline-block;
            padding: 2px 4px;
            background: #f0f0f0;
            border-radius: 4px;
            font-size: 12px;
          }

          .more-dots {
            color: #666;
            margin-left: 2px;
          }

          .label {
            width: 85px;
            flex-shrink: 0;
            color: #86909c;
            font-size: 12px;
          }
          .text {
            font-size: 12px;
          }
        }
      }

      .right {
        width: 50%;
        height: 262px;
        padding-top: 6px;
        .tab-content {
          padding: 0 12px;
          overflow-y: scroll;
          height: 100%;
          .template {
            width: 100%;
            font-size: 13px;
            line-height: 18px;
            margin-bottom: 10px;
          }
        }

        .ant-tabs {
          height: 222px;
          border-radius: 6px;
          border: 1px solid #e1e3e4;

          .ant-tabs-nav {
            padding: 0 12px;
            background: #f1f3f5;
            margin-bottom: 8px;

            .ant-tabs-tab {
              color: #86909c;
            }

            .ant-tabs-tab.ant-tabs-tab-active {
              color: #4238c9;
            }
          }

          .tab-content {
            padding: 0 12px;
            overflow-y: scroll;
            max-height: 170px;
            max-width: 256px;

            .template {
              width: 100%;
              font-size: 12px;
              line-height: 18px;
            }
          }
        }
      }
    }
  }

  .progress-info {
    flex: 1;
    background: #fff;
    border-radius: 8px;
    border: 1px solid #e1e3e4;
    width: 50%;

    .status {
      width: 50px;
      height: 18px;
      border-radius: 9px;
      font-size: 12px;
      text-align: center;
      line-height: 18px;
      flex-shrink: 0;
      margin-right: 10px;
    }

    .execute-box {
      min-height: 248px;

      .execute-tab-box {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding-right: 12px;
        .execute-item-title {
          height: 32px;
          background: #eeeff1;
          display: flex;
          align-items: center;
          .execute-item-title-text {
            font-size: 14px;
            color: #333;
            height: 100%;
            padding: 0 12px;
            display: flex;
            align-items: center;
            cursor: pointer;
          }
          .active {
            color: #4238c9;
            background: #e3e1f5;
          }
        }
        .execute-success-box {
          .success-sum {
            color: #67c23a;
          }
        }
      }
      .execute-tab-content {
        margin-top: 14px;
      }
    }

    .sub-title {
      height: 35px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      border-bottom: 1px solid #e1e3e4;
      padding: 0 12px;

      .left {
        display: flex;

        .return-btn {
          width: 24px;
          height: 24px;
          display: flex;
          justify-content: center;
          justify-items: center;
          align-items: center;
          margin-right: 12px;
          background: #fff;
          border: 0px;

          &:hover {
            border: 1px solid #4238c9;
            color: #4238c9;
          }
        }

        .return-icon {
          font-size: 12px;
        }

        .text {
          margin-left: 10px;
          font-weight: 500;
          font-size: 16px;
          margin-right: 6px;
          line-height: 24px;
        }

        .status {
          width: 50px;
          height: 18px;
          border-radius: 9px;
          font-size: 12px;
          text-align: center;
          line-height: 18px;
          flex-shrink: 0;
          margin-right: 10px;
        }
      }

      .create-time {
        color: '#666';
      }
    }

    .log-content {
      height: 283px;

      .log-panel {
        border-radius: 8px;
        height: 235px;
        display: flex;
        flex-direction: column;

        .header {
          height: 36px;
          border-radius: 8px 8px 0 0;
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 0 16px;

          .left {
            height: 100%;
            display: flex;
            padding-top: 10px;

            span:first-child {
              width: 14px;
              height: 14px;
              background: #ff5f56;
              border-radius: 50%;
            }

            span:nth-child(2) {
              width: 14px;
              height: 14px;
              background: #ffbb2f;
              border-radius: 50%;
              margin: 0 8px;
            }

            span:last-child {
              width: 14px;
              height: 14px;
              background: #27c841;
              border-radius: 50%;
            }
          }

          .copy-btn .ant-typography .copy {
            color: #d0d0d0;

            &:hover {
              color: #fff;
            }
          }
        }

        .content {
          flex: 1;
          border-radius: 0 0 8px 8px;
          padding: 14px 12px;
          overflow-y: scroll;
          display: flex;
          flex-direction: column;

          span {
            color: #fff;
            line-height: 20px;
          }
        }
      }
    }

    .information {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-top: 8px;
      color: #666;
      font-size: 12px;

      .success-sum {
        color: #73a0fa;
      }

      .failed-sum {
        color: #f44f4f;

        &:hover {
          cursor: pointer;
          text-decoration: underline;
        }
      }
    }
  }
`
